<script setup lang="ts">
import { onMounted, onUpdated,onUnmounted, ref, computed, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Trip } from "@/models/Trip";
// Removed static imports of dynamically routed components
// import TripOverview from "@/components/TripOverview/TripOverview.vue";
// import TripSavedOffers from "@/components/TripSavedOffers/TripSavedOffers.vue";
// import TripCrew from "@/components/TripCrew/TripCrew.vue";
// import TripItenary from "@/components/TripItenary/TripItenary.vue";
import InviteCrewMemberModal from "@/components/InviteCrewMemberModal/InviteCrewMemberModal.vue";
import ManuallyAddCrewMemberModal from "@/components/ManuallyAddCrewMemberModal/ManuallyAddCrewMemberModal.vue";
import TripDeleteModal from "@/components/TripCard/TripDeleteModal.vue";
import TripEditModal from "@/components/TripCard/TripEditModal.vue";
import { useI18n } from 'vue-i18n';
import { useTripsStore } from "@/stores/tripsStore";
import TripBudgetOverview from "@/components/TripBudgetOverview/TripBudgetOverview.vue";
import boat1 from "@/assets/about/boat-1.png";
import {useUserStore} from "@/stores/userStore";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
import {useFormatStore} from "@/stores/formatStore";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const tripStore = useTripsStore();
const formatStore = useFormatStore();
const defaultImage = boat1;

const trip = computed(() => tripStore.trip)

const imageUrl = computed(() => {
  const base = trip.value?.backgroundImageUrl || defaultImage;
  if (!base) return '';
  return `${base}?t=${Date.now()}`; // ONLY if trip was just reloaded
});

const loading = ref(false);
const showInviteCrewMemberModal = ref(false);
const manuallyAddCrewMemberModal = ref(false);
const showDeleteModal = ref(false);
const showEditModal = ref(false);
const tripToDelete = ref(null);
const isMobile = ref(false);


const activeTab = ref(
    trip.value?.state === 'active' || trip.value?.state === 'planned' || trip.value?.state === 'done' ? 'overview' : 'saved-offers'
);

const userStore = useUserStore();
const changeTab = (newTab: string) => {
  router.push({
    name: `trip-${newTab}`,
    params: {
      locale: route.params.locale,
      id: route.params.id
    }
  });
};

onMounted(() => {
  const checkMobile = () => {
    isMobile.value = window.innerWidth <= 768;
  };

  checkMobile();
  window.addEventListener("resize", checkMobile);

  onUnmounted(() => {
    window.removeEventListener("resize", checkMobile);
  });
});


watch(
    () => userStore.sessionRestored,
    async (restored) => {
      if (restored) {
        try {
          loading.value = true;

          if (!userStore.user) {
            await userStore.fetchUserProfile();
          }

          if (!tripStore.trip || tripStore.trip.slug !== route.params.id) {
            await tripStore.loadTrip(route.params.id);
          }
        } finally {
          loading.value = false;
        }
      }
    },
    {immediate: true}
);


const onDelete = async (uuid: string) => {
  try {
    await tripStore.removeTrip(uuid);
    showDeleteModal.value = false;
    tripToDelete.value = null;
    await tripStore.getAll();
  } catch (error) {
    console.error(`Failed to delete trip ${uuid}:`, error);
  }
};


const onEdit = async (data: { uuid: string, name: string }) => {
  try {
    await tripStore.updateTrip(data.uuid, { name: data.name });
    showEditModal.value = false;
    tripStore.trip.name = data.name;
    await tripStore.getAll();
  } catch (error) {
    console.error(`Failed to update trip ${data.uuid}:`, error);
  }
};



// We will enable these again in the future
// const activeTab = ref(getActiveTabFromRoute());

// watch(route, () => {
//   activeTab.value = getActiveTabFromRoute();
// });

// function getActiveTabFromRoute() {
//   const pathSegments = route.path.split("/");
//   return pathSegments[pathSegments.length - 1] || "saved-offers";
// }
</script>

<template>
  <div v-if="loading" class="container flex items-center justify-center py-16">
    <CustomSpinner/>
  </div>
  <div class="bg-primary-mouve" v-if="!loading && trip !== null">
    <div class="pt-8">
      <div class="container top flex justify-between">
        <div class="flex items-center gap-4">
          <img src="@/assets/icons/svg/Left_16x16.svg" alt="Back" width="16" class="cursor-pointer"
            @click="$router.push('/my/trips')">
          <!-- Desktop layout -->
          <div class="hidden sm:flex items-center gap-4">
            <img :src="imageUrl" alt="trip"
                 class="w-[200px] h-[130px] object-contain rounded-11xl">
            <div class="responsive-title-3 font-semibold">
              <h1>{{ trip.name }}</h1>
              <div>
                <div class="responsive-body-small font-semibold flex items-center gap-1">
                  <img src="@/assets/icons/svg/Location_16x16.svg" alt="location" class="w-4 h-4" />
                  {{ trip.locationId?.name }}
                </div>
                <div class="responsive-body-xsmall flex items-center gap-1">
                  <img src="@/assets/icons/svg/Calendar_16x16.svg" alt="calendar" class="w-4 h-4" />
                  <div class="mt-1">
                    {{
                      formatStore.formatDateWithCustomPatternMappingLocalized(
                          trip.dateStart,
                          'MMM DD, YYYY'
                      )
                    }}
                    -
                    {{
                      formatStore.formatDateWithCustomPatternMappingLocalized(
                          trip.dateEnd,
                          'MMM DD, YYYY'
                      )
                    }}
                  </div>

                  
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile layout -->
          <div class="sm:hidden w-full h-40 bg-cover bg-center rounded-11xl flex flex-col justify-end p-4 text-white"
               :style="{ backgroundImage: `url(${imageUrl})` }">
            <div class="responsive-title-3 font-semibold">
              {{ trip.name }}
              <div>
                <div class="responsive-body-xsmall font-semibold flex items-center gap-1">
                  <img src="@/assets/icons/svg/Location_16x16.svg" alt="location" class="w-3 h-3" />
                  {{ trip.locationId?.name }}
                </div>
                <div class="responsive-body-xsmall flex items-center gap-1">
                  <img src="@/assets/icons/svg/Calendar_16x16.svg" alt="calendar" class="w-3 h-3" />
                  <div class="mt-0.5">{{ trip.dateStart }} - {{ trip.dateEnd }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          class="border-black border border-solid w-12 h-7 flex justify-center items-center rounded-11xl relative"
        >

          <ElDropdown trigger="click"
                      placement="left"
                      teleported="false"
                      :popper-options="{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'viewport',
                            padding: 20
                          }
                        },
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 10]
                          }
                        }
                      ]
                    }">
            <img src="@/assets/icons/svg/Menu dots_16x16.svg">
            <template #dropdown>
              <ElDropdownMenu>
                <ElDropdownItem class="text-black flex gap-2" @click="showEditModal = true">
                  <img src="@/assets/icons/svg/Edit_24x24.svg" />
                  {{ t('tripCard.renameTrip') }}
                </ElDropdownItem>
                <ElDropdownItem class="text-black flex gap-2" @click="showDeleteModal = true">
                  <img src="@/assets/icons/svg/Delete_24x24.svg" />
                  {{ t('tripCard.deleteTrip') }}
                </ElDropdownItem>
              </ElDropdownMenu>
            </template>
          </ElDropdown>
        </div>
      </div>
      <div class="bottom mt-8">
        <ElTabs class="bg-primary-mouve" :model-value="activeTab" @tab-change="(newTab) => changeTab(newTab)" v-if="trip !== null">
          <div class="mt-6 mb-6 container">
            <ElTabPane v-if="trip.state === 'planned' || trip.state === 'active' || trip.state === 'done'" :label="t('overview')" name="overview" />
            <ElTabPane v-else :label="t('savedOffers')" name="saved-offers" />
            <ElTabPane :label="t('crewMembers')" name="crew" />
<!--            <ElTabPane :label="t('tripBudget')" name="budget"/>-->
            <router-view v-slot="{ Component }">
              <component :is="Component" :trip="trip" />
            </router-view>
          </div>
        </ElTabs>
      </div>
    </div>
  </div>
  <InviteCrewMemberModal @on-close="showInviteCrewMemberModal = false" :open="showInviteCrewMemberModal" />
  <ManuallyAddCrewMemberModal :open="manuallyAddCrewMemberModal" @on-close="manuallyAddCrewMemberModal = false" />
  <TripDeleteModal
      :showDeleteModal="showDeleteModal"
      @on-close="showDeleteModal = false"
      :trip="trip"
      @on-delete-click="onDelete(trip.uuid)"
  />

  <TripEditModal
      v-if="trip"
      :show="showEditModal"
      :name="trip.name"
      :uuid="trip.uuid"
      :backgroundImageUrl="trip.backgroundImageUrl"
      @on-image-updated="async () => {
  await tripStore.loadTrip(route.params.id);
}"
      @on-close="showEditModal = false"
      @on-edit-click="(newName) => onEdit({ uuid: trip.uuid, name: newName })"
  />
</template>

<style scoped>

.border-black {
  border-color: black !important;
}

:deep(.el-tabs__nav-wrap:after) {
  background-color: transparent !important;
}

:deep(.el-tabs__content) {
  padding: 0;
  background-color: #FFF;
  width: 100%;
}

:deep(.el-tabs__item:hover),

:deep(.el-tabs__item.is-active) {
  color: black;
  border: black;
}

:deep(.el-tabs__item.is-active) {
  color: black;
  font-weight: 600;
}

:deep(.el-tabs__item:not(.is-active)) {
  color: #8C8B94;
  font-weight: 600;
}

:deep(.el-tabs__active-bar) {
  background-color: black;
  height: 3px;
}


@media (max-width: 768px) {
  .mainPanel {
    padding: 0;
  }

}

:deep(.el-tabs__header) {
margin-bottom: 0;
}
</style>
